import { cn } from '@/shared/lib/cn';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Icon } from '../assets/icons';
import { Typography } from './typography';

type TagVariant = 'default' | 'green' | 'red' | 'yellow';

type TagProps = {
  text: string;
  variant?: TagVariant;
  id?: string;
  onClose?: (id: string) => void;
} & ComponentPropsWithoutRef<'div'>;

const variantClasses: Record<TagVariant, string> = {
  default: 'bg-gray-400/10 text-black inset-ring-gray-400/20',
  green:
    'bg-polar-green-1 text-polar-green-8 dark:text-polar-green-7 inset-ring-polar-green-3',
  red: 'bg-dust-red-1 text-dust-red-7 inset-ring-dust-red-3',
  yellow:
    'bg-volcano-1 text-volcano-8 dark:text-volcano-7 inset-ring-volcano-3',
};

export const Tag = forwardRef<HTMLDivElement, TagProps>(
  ({ variant = 'default', id, text, className, onClose, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1 rounded-xs px-2 py-[1px] text-xs inset-ring',
          variantClasses[variant],
          className
        )}
        {...props}
      >
        <Typography as="span" variant="footnote-description">
          {text}
        </Typography>
        {id && onClose && (
          <Icon
            name="close-x"
            className="cursor-pointer"
            size={10}
            onClick={() => onClose(id)}
          />
        )}
      </div>
    );
  }
);

Tag.displayName = 'Tag';
